import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, PowerOff, RefreshCw, ShieldCheck, Clock } from "lucide-react";

const SERVICE_LABELS = {
  swedavia_flightinfo: { name: "Swedavia FlightInfo", desc: "Status, gate, terminal för svenska flygplatser" },
  swedavia_waittime:   { name: "Swedavia WaitTime",   desc: "Säkerhetskö-tider" },
  aerodatabox:         { name: "AeroDataBox",          desc: "Utländska flygplatser + webhooks" },
  airlabs:             { name: "AirLabs",              desc: "Routes/airports-databas" },
  opensky:             { name: "OpenSky",              desc: "ADS-B fallback (4 000/dag med OAuth2)" },
  adsblol:             { name: "adsb.lol",             desc: "Live ADS-B (gratis, ingen quota)" },
  commhub:             { name: "Commhub",              desc: "Push, e-post, SMS" },
};

export function ServiceStatusCard() {
  const [data, setData] = useState(null);
  const [quota, setQuota] = useState({});
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        api.get("/flights/services/status"),
        api.get("/flights/quota/usage"),
      ]);
      setData(s.data);
      setQuota(q.data || {});
    } catch (e) {
      toast.error("Kunde inte hämta status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const testService = async (svc) => {
    setTesting((t) => ({ ...t, [svc]: true }));
    try {
      const { data } = await api.post(`/flights/services/${svc}/test`);
      if (data.ok) {
        toast.success(`${SERVICE_LABELS[svc]?.name || svc}: OK — re-aktiverad`);
      } else {
        toast.error(`${SERVICE_LABELS[svc]?.name || svc}: ${data.note || "Fel"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test misslyckades");
    } finally {
      setTesting((t) => ({ ...t, [svc]: false }));
    }
  };

  if (loading && !data) {
    return (
      <div className="card-soft p-4 flex items-center gap-2 text-sm text-[color:var(--text-tertiary)]">
        <Loader2 size={14} className="animate-spin" /> Laddar källor…
      </div>
    );
  }
  if (!data) return null;

  return (
    <section data-testid="service-status-card" className="card-soft p-4 space-y-3">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[color:var(--accent-color,#6366f1)]" />
          <h3 className="text-sm font-semibold">Källor & Auto-disable</h3>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Uppdatera
        </button>
      </header>

      <p className="text-xs text-[color:var(--text-tertiary)]">
        Källor stängs automatiskt av vid HTTP 401/403/429. Klicka <b>Testa</b> för
        att försöka aktivera igen.
      </p>

      <ul className="space-y-1.5">
        {Object.entries(data).map(([svc, info]) => {
          const label = SERVICE_LABELS[svc] || { name: svc, desc: "" };
          const isOn = info.enabled;
          return (
            <li
              key={svc}
              data-testid={`service-row-${svc}`}
              className="rounded-md bg-[color:var(--bg-elevated)] p-2.5 flex items-start gap-2.5"
            >
              <span className="mt-0.5">
                {isOn
                  ? <CheckCircle2 size={16} className="text-emerald-500" />
                  : <PowerOff size={16} className="text-red-500" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-medium">{label.name}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded
                    ${isOn ? "bg-emerald-500/15 text-emerald-700"
                          : "bg-red-500/15 text-red-700"}`}>
                    {isOn ? "Aktiv" : "Avstängd"}
                  </span>
                  {!isOn && info.disabled_until && (
                    <RetryCountdown
                      disabledUntil={info.disabled_until}
                      onExpired={load}
                    />
                  )}
                </div>
                <div className="text-[11px] text-[color:var(--text-tertiary)] mt-0.5">
                  {label.desc}
                </div>
                {!isOn && info.disable_reason && (
                  <div className="flex items-start gap-1 mt-1 text-[11px] text-red-600">
                    <AlertCircle size={11} className="mt-0.5 shrink-0" />
                    <span>{info.disable_reason}</span>
                  </div>
                )}
                {info.last_error && (
                  <div className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5 font-mono truncate">
                    {info.last_error}
                  </div>
                )}
                {info.last_test_at && (
                  <div className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5">
                    Testad: {new Date(info.last_test_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}
                  </div>
                )}
                {/* Quota usage idag */}
                {quota[svc] && (
                  <QuotaBar
                    used={quota[svc].used_today}
                    budget={quota[svc].daily_budget}
                    pct={quota[svc].pct_used}
                    overBudget={quota[svc].over_budget}
                    note={quota[svc].note}
                  />
                )}
              </div>
              <button
                type="button"
                onClick={() => testService(svc)}
                disabled={testing[svc]}
                data-testid={`service-test-${svc}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold bg-indigo-600 text-white shadow-sm border border-indigo-700 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-wait whitespace-nowrap"
              >
                {testing[svc]
                  ? <Loader2 size={12} className="animate-spin text-white" />
                  : <RefreshCw size={12} className="text-white" />}
                <span className="text-white">{testing[svc] ? "Testar…" : "Testa"}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function RetryCountdown({ disabledUntil, onExpired }) {
  /**
   * Visar "Vänta tills HH:MM (Nm kvar)" bredvid AVSTÄNGD-badgen vid
   * temporär disable (429 rate-limit). När timern når noll triggas
   * `onExpired()` så parent-statusen reloadas → badgen försvinner.
   *
   * `disabledUntil` är ISO-timestamp (UTC) från service_status.disabled_until.
   * Tick:ar varje sekund så användaren ser realtids-countdown.
   */
  const [now, setNow] = useState(() => Date.now());
  // Parse disabledUntil säkert (null om ogiltig) — gjort som useState så
  // hooks-ordningen är stabil oavsett input.
  const untilMs = (() => {
    try {
      const t = new Date(disabledUntil).getTime();
      return Number.isFinite(t) ? t : null;
    } catch { return null; }
  })();
  const remainingMs = untilMs == null ? null : untilMs - now;

  // Tick varje sekund — alltid mountad
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // När timern går ut: trigga parent-reload (en gång)
  const expired = remainingMs != null && remainingMs <= 0;
  useEffect(() => {
    if (expired && typeof onExpired === "function") {
      const t = setTimeout(() => onExpired(), 1500);
      return () => clearTimeout(t);
    }
  }, [expired, onExpired]);

  if (untilMs == null) return null;
  if (expired) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-800">
        <RefreshCw size={10} className="animate-spin" /> Återöppnar…
      </span>
    );
  }
  // Format: HH:MM-tidpunkt + "Xm Ys kvar" eller "Xm kvar" om >2 min
  const untilHM = new Date(untilMs).toLocaleTimeString("sv-SE", {
    hour: "2-digit", minute: "2-digit",
  });
  const totalSec = Math.floor(remainingMs / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  const remainingLabel = mins > 2
    ? `${mins} min kvar`
    : mins > 0
      ? `${mins}m ${secs}s kvar`
      : `${secs}s kvar`;
  return (
    <span
      data-testid="retry-countdown"
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-800"
      title={`Försöker igen automatiskt ${untilHM}`}
    >
      <Clock size={10} />
      <span className="tabular-nums">Vänta tills {untilHM}</span>
      <span className="opacity-70 normal-case tracking-normal">· {remainingLabel}</span>
    </span>
  );
}


function QuotaBar({ used, budget, pct, overBudget, note }) {
  if (budget === null || budget === undefined) {
    return (
      <div className="text-[10px] text-[color:var(--text-tertiary)] mt-1">
        Idag: {used} anrop · ingen quota
      </div>
    );
  }
  const tone = overBudget ? "bg-red-500"
    : pct >= 80 ? "bg-amber-500"
    : pct >= 50 ? "bg-emerald-400"
    : "bg-emerald-500";
  return (
    <div className="mt-1 space-y-0.5">
      <div className="flex items-baseline justify-between text-[10px]">
        <span className="text-[color:var(--text-tertiary)]">
          Idag: <span className="font-semibold tabular-nums">{used}</span> / {budget}
          {note && <span className="opacity-60"> · {note}</span>}
        </span>
        <span className={`tabular-nums ${overBudget ? "text-red-600 font-semibold" : "text-[color:var(--text-tertiary)]"}`}>
          {pct}%
        </span>
      </div>
      <div className="h-1 rounded-full bg-[color:var(--bg-elevated)] overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {overBudget && (
        <div className="text-[10px] text-red-600">
          Över budget — överväg att öka poll-intervall
        </div>
      )}
    </div>
  );
}
